import { AlertCircle, ChevronDown, RefreshCw, Search, X } from "lucide-react";
import { useState } from "react";

import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";
import {
  CORRIDOR_OPTIONS,
  DATE_RANGE_OPTIONS,
  STATUS_FILTER_OPTIONS,
  STATUS_LABEL_KEYS,
} from "@/modules/dashboard/payments-activity/constants/paymentsActivity.constants";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaCombobox } from "@/shared/components/forms/ColocaCombobox";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { ColocaTabs, type ColocaTabItem } from "@/shared/components/navigation/ColocaTabs";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { SWAP_STATUS_LABEL_KEYS, SwapRuntimeStatus, isUuid } from "../constants/paymentsLedger.constants";
import type { UseLedgerOperationPickerResult } from "../hooks/useLedgerOperationPicker";
import { useOperationKindLookup } from "../hooks/useOperationKindLookup";
import type { LedgerOperationCandidate, LedgerOperationKind } from "../types/paymentsLedger.types";
import { LedgerStatusPill } from "./LedgerStatusPill";

export interface LedgerOperationPickerProps {
  picker: UseLedgerOperationPickerResult;
  organizations: Organization[];
  organizationsDisabled: boolean;
  organizationNames: Record<string, string>;
  /** Currently open operation, so its row reads as selected. */
  selectedId: string | null;
  onSelect: (kind: LedgerOperationKind, operationId: string) => void;
}

/**
 * Left pane: how an operation gets chosen.
 *
 * Two ways in, because reconciliation arrives from both directions. Browsing — tab per runtime endpoint plus
 * the server-side filters each one actually supports — and a direct ID lookup, which is what happens when the
 * id comes from a support ticket or a provider statement.
 *
 * The lookup does not filter the list and does not care which tab is open: nothing in a uuid says whether it
 * is a payout, a payin or a swap, so `useOperationKindLookup` asks the journal which type holds entries for
 * it and opens that one. Pasting a payin id while on the Payouts tab used to land on an empty journal that
 * was indistinguishable from a real operation with no entries yet.
 *
 * There is deliberately no free-text search: none of the three list endpoints has one.
 */
export function LedgerOperationPicker({
  picker,
  organizations,
  organizationsDisabled,
  organizationNames,
  selectedId,
  onSelect,
}: LedgerOperationPickerProps) {
  const { t, activeLanguage } = useTranslations("paymentsLedger");
  const { t: tActivity } = useTranslations("paymentsActivity");
  const [search, setSearch] = useState("");
  /** `null` = nothing to say yet; the rest each have their own message under the field. */
  const [searchIssue, setSearchIssue] = useState<"invalid" | "not-found" | "failed" | null>(null);
  const kindLookup = useOperationKindLookup();

  const tabs: ColocaTabItem[] = [
    { value: "payouts", label: t("picker_tab_payouts"), content: null },
    { value: "payins", label: t("picker_tab_payins"), content: null },
    { value: "swaps", label: t("picker_tab_swaps"), content: null },
  ];

  const submitSearch = async () => {
    const value = search.trim();
    if (!isUuid(value)) {
      setSearchIssue(value.length > 0 ? "invalid" : null);
      return;
    }
    setSearchIssue(null);

    // The journal names the kind — the active tab is not evidence of what a pasted id belongs to.
    const operationId = value.toLowerCase();
    const lookup = await kindLookup.resolve(operationId);
    if (lookup.status === "resolved") {
      onSelect(lookup.kind, operationId);
      return;
    }
    // Nothing to open: staying put with a reason beats landing on an empty journal under the wrong tab.
    setSearchIssue(lookup.status);
  };

  // Payins and payouts share the activity status wording; swaps have their own three-value vocabulary.
  const statusLabel = (status: string) => {
    if (picker.kind === "swaps") {
      const key = SWAP_STATUS_LABEL_KEYS[status];
      return key ? t(key) : status;
    }
    const key = STATUS_LABEL_KEYS[status];
    return key ? tActivity(key) : status;
  };

  const statusOptions: readonly string[] =
    picker.kind === "swaps" ? Object.values(SwapRuntimeStatus) : STATUS_FILTER_OPTIONS[picker.kind];

  const statusItems = [
    { label: t("picker_filter_all_statuses"), onClick: () => picker.setStatus(null) },
    ...statusOptions.map((status) => ({
      label: statusLabel(status),
      onClick: () => picker.setStatus(status),
    })),
  ];

  const corridorItems = [
    { label: t("picker_filter_all_corridors"), onClick: () => picker.setCountry(null) },
    ...CORRIDOR_OPTIONS.map((corridor) => ({
      label: `${corridor.country} · ${corridor.rail} · ${corridor.currency}`,
      onClick: () => picker.setCountry(corridor.country),
    })),
  ];

  const rangeItems = [
    { label: t("picker_filter_all_dates"), onClick: () => picker.setRangeDays(null) },
    ...DATE_RANGE_OPTIONS.map((days) => ({
      label: t("picker_filter_last_days", { days }),
      onClick: () => picker.setRangeDays(days),
    })),
  ];

  const organizationOptions = organizations.map((organization) => ({
    value: organization.id,
    label: organization.name,
  }));

  const from = picker.total === 0 ? 0 : (picker.page - 1) * picker.pageSize + 1;
  const to = Math.min(picker.page * picker.pageSize, picker.total);

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{t("picker_title")}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("picker_description")}</p>
      </div>

      <ColocaTabs
        tabs={tabs}
        variant="line"
        value={picker.kind}
        onValueChange={(value) => picker.setKind(value as LedgerOperationKind)}
        listClassName="overflow-x-auto"
      />

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <ColocaSearchInput
            value={search}
            onValueChange={(value) => {
              setSearch(value);
              setSearchIssue(null);
            }}
            placeholder={t("picker_search_placeholder")}
            onKeyDown={(event) => {
              if (event.key === "Enter") void submitSearch();
            }}
            disabled={kindLookup.isResolving}
            className="flex-1"
          />
          <ColocaButton
            variant="default"
            size="sm"
            icon={Search}
            loading={kindLookup.isResolving}
            aria-label={t("picker_search_placeholder")}
            onClick={() => void submitSearch()}
          />
        </div>
        <p
          aria-live="polite"
          className={cn("text-[11px]", searchIssue ? "text-destructive" : "text-muted-foreground")}
        >
          {kindLookup.isResolving
            ? t("picker_search_resolving")
            : searchIssue === "invalid"
              ? t("picker_search_invalid")
              : searchIssue === "not-found"
                ? t("picker_search_not_found")
                : searchIssue === "failed"
                  ? t("picker_search_failed")
                  : t("picker_search_hint")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full">
          <ColocaCombobox
            value={picker.filters.organizationId}
            onValueChange={picker.setOrganizationId}
            options={organizationOptions}
            placeholder={t("picker_filter_all_orgs")}
            disabled={organizationsDisabled}
            aria-label={t("picker_filter_org_label")}
          />
        </div>
        <ColocaDropdownMenu
          trigger={
            <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
              {picker.filters.status ? statusLabel(picker.filters.status) : t("picker_filter_status_label")}
            </ColocaButton>
          }
          items={statusItems}
          align="start"
        />
        {/* Swaps have no corridor — `country` is not a parameter of their list endpoint. */}
        {picker.kind !== "swaps" && (
          <ColocaDropdownMenu
            trigger={
              <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
                {picker.filters.country ?? t("picker_filter_corridor_label")}
              </ColocaButton>
            }
            items={corridorItems}
            align="start"
          />
        )}
        <ColocaDropdownMenu
          trigger={
            <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
              {picker.filters.rangeDays
                ? t("picker_filter_last_days", { days: picker.filters.rangeDays })
                : t("picker_filter_date_label")}
            </ColocaButton>
          }
          items={rangeItems}
          align="start"
        />
        {picker.hasActiveFilters && (
          <ColocaButton variant="ghost" size="sm" icon={X} onClick={picker.clearFilters}>
            {t("picker_clear")}
          </ColocaButton>
        )}
      </div>

      {picker.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("picker_error")}
              <ColocaButton
                variant="default"
                size="xs"
                icon={RefreshCw}
                onClick={() => void picker.refresh()}
              >
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      ) : picker.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((index) => (
            <ColocaSkeleton key={index} className="h-16 rounded-xl" />
          ))}
        </div>
      ) : picker.candidates.length === 0 ? (
        <ColocaEmpty
          icon={Search}
          title={t("picker_empty_title")}
          description={t("picker_empty_description")}
        >
          {picker.hasActiveFilters && (
            <ColocaButton variant="default" size="sm" icon={X} onClick={picker.clearFilters}>
              {t("picker_clear")}
            </ColocaButton>
          )}
        </ColocaEmpty>
      ) : (
        <>
          <ul className="space-y-2">
            {picker.candidates.map((candidate) => (
              <li key={candidate.id}>
                <OperationRow
                  candidate={candidate}
                  organizationName={organizationNames[candidate.organizationId] ?? null}
                  isSelected={candidate.id === selectedId}
                  locale={activeLanguage}
                  unknownOrgLabel={t("picker_org_unknown")}
                  onSelect={() => onSelect(candidate.kind, candidate.id)}
                />
              </li>
            ))}
          </ul>
          <ColocaPagination
            page={picker.page}
            perPage={picker.pageSize}
            total={picker.total}
            size="sm"
            onPageChange={picker.setPage}
            summary={
              <span className="text-xs text-muted-foreground tabular-nums">
                {t("picker_summary", { from, to, total: picker.total })}
              </span>
            }
          />
        </>
      )}
    </div>
  );
}

interface OperationRowProps {
  candidate: LedgerOperationCandidate;
  organizationName: string | null;
  isSelected: boolean;
  locale: string;
  unknownOrgLabel: string;
  onSelect: () => void;
}

function OperationRow({
  candidate,
  organizationName,
  isSelected,
  locale,
  unknownOrgLabel,
  onSelect,
}: OperationRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isSelected}
      className={cn(
        "w-full rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isSelected
          ? "border-primary bg-primary/5"
          : "border-border bg-card hover:border-ring/40 hover:bg-muted/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-foreground">
            {organizationName ?? unknownOrgLabel}
          </span>
          <span className="block truncate text-[11px] text-muted-foreground">{candidate.subtitle}</span>
        </span>
        <LedgerStatusPill candidate={candidate} className="shrink-0" />
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <ColocaAmountDisplay
          value={candidate.amount}
          currency={candidate.currency}
          size="sm"
          locale={locale}
        />
        <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
          {formatDateTime(candidate.createdAt, { locale, includeTime: true })}
        </span>
      </div>
    </button>
  );
}
